import React, { memo } from 'react';

import { Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClickablePhone from 'app/shared/components/ClickablePhone';
import { CheckboxCDS, RadioGroupCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType, PHONE_FIELD_INPUT_TYPES } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { normalizeViewSSN } from 'utils/data';
import { formatDate } from 'utils/date';
import { formatNumberToCurrencyString } from 'utils/number';

import { getSanitizedRichTextHtml } from './ContentViewGenerator.utils';

const getStyles = theme => {
  const { palette } = theme;

  return {
    label: {
      color: palette.primary.main,
      fontWeight: 800,
    },
  };
};

const getFormattedValue = ({ type, value, format, options }) => {
  if (_.isNil(value)) {
    return '';
  }

  switch (type) {
    case FieldInputType.CURRENCY:
      return formatNumberToCurrencyString(value, format);

    case FieldInputType.DATE: {
      return formatDate(value, format, options);
    }

    case FieldInputType.BOOL:
      return value ? 'Yes' : 'No';

    case FieldInputType.PERCENTAGE:
      return `${value}%`;

    case FieldInputType.SSN: {
      return normalizeViewSSN(value);
    }

    case FieldInputType.TEL:
    case FieldInputType.TEL_EXT:
      return <ClickablePhone emptyFallback="" value={value} />;

    case FieldInputType.RICH_TEXT:
      return <div dangerouslySetInnerHTML={getSanitizedRichTextHtml(value)} />;

    default:
      return value;
  }
};

const ContentGenerator = ({
  label,
  value,
  dataTestId = '',
  contentValueClassName = '',
  contentLabelClassName = '',
  checkboxClassName = '',
  isLabelTooltip = false,
  isValueTooltip = true,
  isLabelBlurred = false,
  isValueBlurred = false,
  name,
  renderContent,
  ...restProps
}) => {
  const theme = useTheme();
  const classes = getStyles(theme);

  if (restProps.fieldType === FieldType.CHECKBOX) {
    return (
      <CheckboxCDS
        disabled
        containerClassName={checkboxClassName}
        dataTestId={dataTestId}
        label={label}
        value={value}
      />
    );
  }

  if (restProps.fieldType === FieldType.RADIO) {
    return (
      <RadioGroupCDS {...restProps} disabled dataTestId={dataTestId} name={name} value={value} />
    );
  }

  const formattedValue = getFormattedValue({ value, ...restProps });
  const valueToRender = renderContent ? renderContent(value) : formattedValue;
  const isPhoneValue = !renderContent && PHONE_FIELD_INPUT_TYPES.includes(restProps.type);
  // A phone value renders its own call affordance + tooltip, so the truncation tooltip would nest.
  const isTruncationTooltipShown = isValueTooltip && !isPhoneValue;

  return (
    <>
      {!!label && (
        <div
          className={clsx(
            classes.label,
            'w-1/2',
            'pr-5',
            'content-view-generator__field-label',
            `content-view-generator__field-label-${name}`,
            isLabelBlurred && 'tool__blur-all',
            contentLabelClassName,
          )}
        >
          {isLabelTooltip ? (
            <Tooltip placement="bottom" title={label}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )}
        </div>
      )}

      <div
        className={clsx(
          !!label && 'w-1/2 pl-5',
          'content-view-generator__field-value',
          `content-view-generator__field-value-${name}`,
          isValueBlurred && 'tool__blur-all',
          contentValueClassName,
        )}
        data-testid={dataTestId}
      >
        {isTruncationTooltipShown ? (
          <Tooltip placement="bottom" title={valueToRender}>
            <span>{valueToRender}</span>
          </Tooltip>
        ) : (
          valueToRender
        )}
      </div>
    </>
  );
};

ContentGenerator.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  contentValueClassName: PropTypes.string,
  contentLabelClassName: PropTypes.string,
  checkboxClassName: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  options: PropTypes.object,
  dataTestId: PropTypes.string,
  isLabelTooltip: PropTypes.bool,
  isValueTooltip: PropTypes.bool,
  isLabelBlurred: PropTypes.bool,
  isValueBlurred: PropTypes.bool,
  renderContent: PropTypes.func,
};

export default memo(ContentGenerator);
